<template>
  <!-- Le formulaire naît au montage (reset) : rien à afficher avant -->
  <div v-if="form" class="manufacturer-settings">
    <!-- Son nom et les marques annoncées par ses bornes (ses alias) -->
    <v-card outlined class="mb-4">
      <v-card-text>
        <div class="d-flex align-center flex-wrap" style="gap: 12px">
          <v-text-field v-if="renaming" v-model="form.name" :label="$t('manufacturer.sheet.name')" outlined dense hide-details autofocus style="max-width: 360px" />
          <div v-else class="text-h5 text--primary flex-grow-1">{{ form.name }}</div>
          <v-btn v-if="canEditManufacturer && !renaming" small outlined @click="renaming = true">{{ $t('manufacturer.sheet.rename') }}</v-btn>
        </div>
        <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px">
          <span class="text-caption text--secondary">{{ $t('manufacturer.sheet.aliases') }}</span>
          <v-chip v-for="alias in form.aliases" :key="alias" small :close="canEditManufacturer && form.aliases.length > 1" @click:close="removeAlias(alias)">
            {{ alias }}<span class="text--secondary ml-1">· {{ sheet.aliasCounts[alias] || 0 }}</span>
          </v-chip>
          <v-text-field
              v-if="canEditManufacturer"
              v-model="newAlias"
              :placeholder="$t('manufacturer.sheet.add-alias')"
              append-icon="mdi-plus"
              outlined
              dense
              hide-details
              style="max-width: 220px"
              @click:append="addAlias"
              @keydown.enter.prevent="addAlias"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- Comment on transfère un fichier à ses bornes, et le suivi automatique de la dernière version -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">{{ $t('manufacturer.sheet.settings') }}</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-select v-model="form.firmwareProtocol" :items="protocols" :label="$t('manufacturer.sheet.firmware-protocol')" :disabled="!canEditManufacturer" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="form.diagnosticProtocol" :items="protocols" :label="$t('manufacturer.sheet.diagnostic-protocol')" :disabled="!canEditManufacturer" outlined dense />
          </v-col>
        </v-row>
        <div class="font-weight-medium mb-1">{{ $t('manufacturer.sheet.rules') }}</div>
        <div v-for="(rule, index) in form.modelRules" :key="index" class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
          <v-combobox
              v-model="rule.models"
              :items="modelNames"
              :label="$t('manufacturer.sheet.rule-models')"
              :disabled="!canEditManufacturer"
              multiple
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              style="flex: 2 1 260px"
          />
          <v-select v-model="rule.firmwareProtocol" :items="ruleProtocols" :label="$t('manufacturer.sheet.firmware-protocol')" :disabled="!canEditManufacturer" outlined dense hide-details style="flex: 1 1 160px" />
          <v-select v-model="rule.diagnosticProtocol" :items="ruleProtocols" :label="$t('manufacturer.sheet.diagnostic-protocol')" :disabled="!canEditManufacturer" outlined dense hide-details style="flex: 1 1 160px" />
          <v-btn v-if="canEditManufacturer" icon small :aria-label="$t('delete')" @click="form.modelRules.splice(index, 1)"><v-icon small>mdi-close</v-icon></v-btn>
        </div>
        <v-btn v-if="canEditManufacturer" small text @click="form.modelRules.push({models: [], firmwareProtocol: null, diagnosticProtocol: null})">
          <v-icon small left>mdi-plus</v-icon>{{ $t('manufacturer.sheet.add-rule') }}
        </v-btn>
        <div class="text-caption text--secondary mt-2">{{ $t('manufacturer.sheet.settings-hint') }}</div>
        <!-- Phase 2 : le parc suit tout seul la plus haute version validée, avec les garde-fous des mises à jour groupées. -->
        <v-divider class="my-4" />
        <!-- Wallbox : la borne va chercher elle-même la dernière version chez le constructeur ; on lui envoie une adresse, pas un fichier -->
        <v-switch v-model="form.managedUpdates" :disabled="!canEditManufacturer" @change="value => value && (form.keepUpToDate = false)" :label="$t('manufacturer.sheet.managed-updates')" inset hide-details class="mt-0" />
        <div class="text-caption text--secondary mt-1">{{ $t('manufacturer.sheet.managed-updates-hint') }}</div>
        <v-text-field
            v-if="form.managedUpdates"
            v-model="form.managedUpdateLocation"
            :label="$t('manufacturer.sheet.managed-location')"
            :hint="$t('manufacturer.sheet.managed-location-hint')"
            :disabled="!canEditManufacturer"
            persistent-hint
            outlined
            dense
            class="mt-3 mono"
        />
        <v-divider class="my-4" />
        <v-switch v-model="form.keepUpToDate" :disabled="!canEditManufacturer || form.managedUpdates" :label="$t('manufacturer.sheet.keep-up-to-date')" inset hide-details class="mt-0" />
        <div class="text-caption text--secondary mt-1">{{ $t('manufacturer.sheet.keep-up-to-date-hint') }}</div>
        <div v-if="form.managedUpdates" class="text-caption orange--text mt-1">{{ $t('manufacturer.sheet.keep-up-to-date-managed') }}</div>
        <v-alert v-if="form.keepUpToDate && !sheet.keepUpToDate" type="warning" text dense class="mt-2 mb-0">{{ $t('manufacturer.sheet.keep-up-to-date-warning') }}</v-alert>
      </v-card-text>
    </v-card>

    <!-- Zone sensible : sur ce constructeur tout entier, irréversible -->
    <v-card v-if="canEditManufacturer" outlined class="mb-4 danger-zone">
      <v-card-title class="text-subtitle-1 error--text">{{ $t('manufacturer.sheet.danger-zone') }}</v-card-title>
      <v-card-text>
        <v-alert v-if="dirty" type="info" text dense>{{ $t('manufacturer.sheet.danger-zone-dirty') }}</v-alert>
        <div class="text-body-2 mb-2">{{ $t('manufacturer.sheet.merge-hint') }}</div>
        <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
          <v-autocomplete
              v-model="mergeTarget"
              :items="otherManufacturers"
              item-text="name"
              item-value="id"
              :label="$t('manufacturer.merge-into')"
              :disabled="dirty"
              outlined
              dense
              hide-details
              style="max-width: 320px"
              @focus="loadOthers"
          />
          <v-btn small outlined color="error" :disabled="!mergeTarget || dirty" :loading="mergingManufacturer" @click="mergeInto">{{ $t('manufacturer.merge') }}</v-btn>
        </div>
        <div class="text-body-2 mb-2">{{ canDelete ? $t('manufacturer.sheet.delete-hint') : $t('manufacturer.sheet.delete-blocked', {types: typesCount, firmwares: sheet.firmwares.length}) }}</div>
        <v-btn small outlined color="error" :disabled="!canDelete || dirty" :loading="deleting" @click="remove">{{ $t('manufacturer.sheet.delete') }}</v-btn>
      </v-card-text>
    </v-card>

    <v-snackbar :value="dirty" timeout="-1" color="white" light>
      <span class="text--primary">{{ $t('manufacturer.sheet.unsaved') }}</span>
      <template #action>
        <v-btn small text @click="reset">{{ $t('cancel') }}</v-btn>
        <v-btn small color="primary" :loading="saving" @click="save">{{ $t('manufacturer.sheet.save') }}</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ManufacturerRepository from "@repository/ManufacturerRepository";
import ProductLineRepository from "@repository/ProductLineRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";

const PROTOCOLS = ['https', 'http', 'ftp', 'ftps', 'sftp']

/**
 * Les réglages d'un constructeur : son nom, les marques annoncées qui sont lui (ses alias), le transfert des fichiers
 * (par défaut et par modèle), le suivi de la dernière version ; tout s'enregistre ensemble. Puis le fusionner dans un
 * autre ou le supprimer.
 */
export default {
  mixins: [FirmwareMixin],

  data() {
    return {
      form: null,
      saved: null,
      newAlias: '',
      renaming: false,
      saving: false,
      deleting: false,
      typesCount: null,
      otherManufacturers: [],
      mergeTarget: null,
      mergingManufacturer: false,
    }
  },

  computed: {
    id() {
      return this.$route.params.id
    },
    sheet() {
      return this.$parent.sheet
    },
    // Supprimable : ni firmware, ni type (ses types se rattacheraient à rien ; on fusionne les doublons).
    canDelete() {
      return !this.sheet.firmwares.length && this.typesCount === 0
    },
    dirty() {
      return !!this.form && JSON.stringify(this.form) !== this.saved
    },
    protocols() {
      return PROTOCOLS.map(value => ({value, text: value.toUpperCase()}))
    },
    ruleProtocols() {
      return [{value: null, text: this.$t('manufacturer.sheet.as-manufacturer')}, ...this.protocols]
    },
    modelNames() {
      return this.sheet.models.map(model => model.model).filter(Boolean)
    },
  },

  watch: {
    sheet() {
      this.reset()
    },
  },

  mounted() {
    this.reset()
    // Ses types (par ses gammes) : un constructeur qui en a ne se supprime pas.
    ProductLineRepository.ofManufacturer(this.id).then(data => {
      this.typesCount = data.lines.reduce((sum, line) => sum + line.types.length, 0)
    }).catch(() => {})
  },

  methods: {
    reset() {
      this.form = {
        name: this.sheet.name,
        aliases: [...this.sheet.aliases],
        firmwareProtocol: this.sheet.firmwareProtocol,
        diagnosticProtocol: this.sheet.diagnosticProtocol,
        modelRules: this.sheet.modelRules.map(rule => ({...rule, models: [...rule.models]})),
        keepUpToDate: !!this.sheet.keepUpToDate,
        managedUpdates: !!this.sheet.managedUpdates,
        managedUpdateLocation: this.sheet.managedUpdateLocation || '',
      }
      this.saved = JSON.stringify(this.form)
      this.renaming = false
      this.newAlias = ''
    },

    addAlias() {
      const alias = this.newAlias.trim()
      if (alias && !this.form.aliases.some(existing => existing.toLowerCase() === alias.toLowerCase())) {
        this.form.aliases.push(alias)
      }
      this.newAlias = ''
    },

    // Retirer une marque qui a des bornes : elles n'ont plus de constructeur (marques à rattacher) ; on le dit d'abord.
    removeAlias(alias) {
      const count = this.sheet.aliasCounts[alias] || 0
      const drop = () => {
        this.form.aliases = this.form.aliases.filter(existing => existing !== alias)
      }
      if (!count) {
        drop()
        return
      }
      this.$dialog.confirm({text: this.$tc('manufacturer.sheet.remove-alias-confirm', count, {alias, count})}).then(result => {
        if (result) {
          drop()
        }
      })
    },

    save() {
      this.saving = true
      ManufacturerRepository.save({id: this.id, ...this.form}).then(() => {
        this.notifySuccess('manufacturer.sheet.saved')
        return this.$parent.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.saving = false
      })
    },

    loadOthers() {
      if (this.otherManufacturers.length) {
        return
      }
      ManufacturerRepository.list().then(tab => {
        this.otherManufacturers = tab.manufacturers.filter(manufacturer => manufacturer.id !== this.id)
      }).catch(err => this.notifyError(err))
    },

    mergeInto() {
      const target = this.otherManufacturers.find(manufacturer => manufacturer.id === this.mergeTarget)
      this.$dialog.confirm({text: this.$t('manufacturer.sheet.merge-confirm', {name: this.sheet.name, target: target ? target.name : ''})}).then(result => {
        if (result) {
          this.doMergeInto()
        }
      })
    },

    doMergeInto() {
      this.mergingManufacturer = true
      ProductLineRepository.mergeManufacturer(this.id, this.mergeTarget).then(() => {
        this.notifySuccess('manufacturer.merged')
        this.$router.push({name: 'manufacturer', params: {id: this.mergeTarget}})
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.mergingManufacturer = false
      })
    },

    remove() {
      this.$dialog.confirm({text: this.$t('manufacturer.sheet.delete-confirm', {name: this.sheet.name})}).then(result => {
        if (result) {
          this.doRemove()
        }
      })
    },

    doRemove() {
      this.deleting = true
      ManufacturerRepository.delete(this.id).then(() => {
        this.$router.push({name: 'manufacturers'})
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.deleting = false
      })
    },
  },
}
</script>
